import { z } from 'zod';
import { BaseSchema } from '../base/base.entity.model';
import * as FlatModels from '../../internal';
import { TranslateService } from "@ngx-translate/core";

export const MediaType = z.enum([
  'MediaDocument',
  'MediaImage',
  'MediaDeepzoom',
  'MediaVideo',
  'MediaAudio',
]);
export type MediaType = z.infer<typeof MediaType>;

/**
 * Media Type Array
 */
export const MediaTypeArray = (translate: TranslateService) => { return Object.values(MediaType.enum).map(value => { return { value: value, name: translate.instant('enum.media.' + value) }; }) };

export const MediaAccess = z.enum([
  'public',
  'private',
]);
export type MediaAccess = z.infer<typeof MediaAccess>;

export const MediaAccessArray = Object.values(MediaAccess.enum).map(el => ({value: el, name: 'enum.media.' + el}));

export const CopyrightPosition = z.enum([
  'watermark_position_top_left',
  'watermark_position_top_right',
  'watermark_position_bot_right',
  'watermark_position_bot_left',
  'watermark_position_none'
]);
export type CopyrightPosition = z.infer<typeof CopyrightPosition>;

export const CopyrightPositionArray = Object.values(CopyrightPosition.enum).map(el => ({value: el, name: 'enum.media.' + el}));

export const CopyrightPositionIcon: Record<string, string> = {
  [CopyrightPosition.enum.watermark_position_top_left]: FlatModels.ButtonDesignModels.ButtonDefaultIcons.WATERMARK_TOP_LEFT,
  [CopyrightPosition.enum.watermark_position_top_right]: FlatModels.ButtonDesignModels.ButtonDefaultIcons.WATERMARK_TOP_RIGHT,
  [CopyrightPosition.enum.watermark_position_bot_right]: FlatModels.ButtonDesignModels.ButtonDefaultIcons.WATERMARK_BOT_RIGHT,
  [CopyrightPosition.enum.watermark_position_bot_left]: FlatModels.ButtonDesignModels.ButtonDefaultIcons.WATERMARK_BOT_LEFT,
  [CopyrightPosition.enum.watermark_position_none]: FlatModels.ButtonDesignModels.ButtonDefaultIcons.WATERMARK_NONE,
}

export const MediaCopyrightSchema = z.object({
  id: z.string().uuid(),
  media: z.string().uuid(),
  copyright: z.string().uuid(),
  order: z.number().default(0),
  text: z.string().nullable().default(null)
});

export type MediaCopyright = z.infer<typeof MediaCopyrightSchema>;

export const MediaSchema = BaseSchema.extend({
  name: z.string().max(255),
  originalFile: z.string().max(255).nullable().default(null),
  file: z.string().max(255),
  thumbnail: z.string().max(255).nullable().default(null),
  type: MediaType,
  altText: z.string().max(255),
  mime: z.string().max(255),
  size: z.number(),
  hidden: z.boolean().default(false),
  access: MediaAccess.default(MediaAccess.enum.public),
  checksum: z.string().max(255).nullable().default(null),
  uploadedBy: z.string().uuid().nullable().optional(),
  uploadedAt: z.coerce.date(),
  createdAt: z.coerce.date(),
  editedAt: z.coerce.date().optional(),
  mediaCopyrights: z.array(MediaCopyrightSchema).optional(),
  stain: z.string().uuid().nullable().optional(),
  compoundNumber: z.string().max(255).nullable().default(null),
  mediaInFolders: z.array(z.string().uuid()).optional(),
  mediaInImages: z.array(z.string().uuid()).optional(),
  mediaInMetadata: z.string().uuid().optional(),
  mediaInSlides: z.array(z.string().uuid()).optional(),
  mediaInSlideThumbs: z.array(z.string().uuid()).optional(),
  mediaInPages: z.array(z.string().uuid()).optional(),
  mediaInGridPdf: z.string().uuid().optional(),
  mediaInSlidePdf: z.string().uuid().optional(),
  authorNote: z.string().nullable().optional(),
});

export type Media = z.infer<typeof MediaSchema>;

export const MediaDocumentSchema = MediaSchema.extend({
  type: z.literal('MediaDocument'),
});

export type MediaDocument = z.infer<typeof MediaDocumentSchema>;

export const MediaAudioSchema = MediaSchema.extend({
  type: z.literal('MediaAudio'),
  duration: z.number().optional(),
});

export type MediaAudio = z.infer<typeof MediaAudioSchema>;

export const MediaVideoSchema = MediaSchema.extend({
  type: z.literal('MediaVideo'),
  duration: z.number().optional(),
  dimensions: FlatModels.DimensionSharedModels.DimensionsSchema.optional(),
  poster: z.string().optional().nullable(),
});

export type MediaVideo = z.infer<typeof MediaVideoSchema>;

export const MediaImageSchema = MediaSchema.extend({
  type: z.literal('MediaImage'),
  dimensions: FlatModels.DimensionSharedModels.DimensionsSchema.optional().nullable(),
});

export type MediaImage = z.infer<typeof MediaImageSchema>;

export const MediaDeepzoomSchema = MediaSchema.extend({
  type: z.literal('MediaDeepzoom'),
  dimensions: FlatModels.DimensionSharedModels.DimensionsSchema.optional(),
  tileFolder: z.string().optional(),
});

export type MediaDeepzoom = z.infer<typeof MediaDeepzoomSchema>;

export const MediaEntityTypeSchema = z.discriminatedUnion('type', [
  MediaDocumentSchema,
  MediaAudioSchema,
  MediaVideoSchema,
  MediaImageSchema,
  MediaDeepzoomSchema,
]);

export type MediaEntityType = z.infer<typeof MediaEntityTypeSchema>;

export const MediaEntityQueryResultSchema = FlatModels.EntityControlModels.EntityQueryResultSchema.extend({
  entity: MediaEntityTypeSchema,
});

export type MediaEntityQueryResult = z.infer<typeof MediaEntityQueryResultSchema>;
